import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { submitBugReport } from '../api/api';
import {
  BUG_CATEGORIES,
  BUG_REPORT_MAX_BYTES,
  BUG_REPORT_MAX_CHARS,
  BUG_REPORT_MAX_IMAGES,
  BUG_REPORT_MIN_CHARS,
  resolveBugIssuePage,
} from '../constants/bugReport';

const C = {
  red: '#E8192C',
  orange: '#FF6B2B',
  green: '#3FCB7E',
  bg: '#0C0905',
  bgWarm: '#100B07',
  bgWarmer: '#15100B',
  card: '#111111',
  cardAlt: '#16110D',
  border: '#1e1e1e',
  borderBright: '#2A2520',
  text: '#F0F2F5',
  textDim: '#9A938B',
  textMuted: '#5C554E',
};

const F = {
  display: '"Bebas Neue", "Oswald", Impact, sans-serif',
  body: '"Rajdhani", "Inter", system-ui, sans-serif',
};

const extractErrorMessage = (error) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return 'Something went wrong. Please try again.';
};

function Eyebrow({ children }) {
  return <div className="rb-eyebrow"><span className="rb-diamond" />{children}</div>;
}

const GRAIN_BG = 'url("data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22160%22 height=%22160%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%222%22 stitchTiles=%22stitch%22/><feColorMatrix values=%220 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.5 0%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>")';

function DriftField({ count = 18, fixed = false }) {
  const [diamonds, setDiamonds] = useState(() =>
    Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 24 + Math.random() * 70,
      rot: Math.random() * 360,
      vx: (Math.random() - 0.5) * 0.04,
      vy: (Math.random() - 0.5) * 0.03 - 0.005,
      vrot: (Math.random() - 0.5) * 0.15,
      opacity: 0.04 + Math.random() * 0.08,
      color: Math.random() > 0.8 ? C.orange : C.red,
    }))
  );

  useEffect(() => {
    let raf;
    let lastTick = performance.now();
    const tick = (now) => {
      const dt = Math.min(50, now - lastTick);
      lastTick = now;
      const step = dt / 16.67;
      setDiamonds((prev) =>
        prev.map((d) => {
          let nx = d.x + d.vx * step;
          let ny = d.y + d.vy * step;
          if (nx > 110) nx = -10;
          if (nx < -10) nx = 110;
          if (ny > 110) ny = -10;
          if (ny < -10) ny = 110;
          return { ...d, x: nx, y: ny, rot: d.rot + d.vrot * step };
        })
      );
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      aria-hidden="true"
      style={{
        position: fixed ? 'fixed' : 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: fixed ? 1 : 0,
      }}
    >
      {diamonds.map((d) => (
        <span
          key={d.id}
          style={{
            position: 'absolute',
            left: `${d.x}%`,
            top: `${d.y}%`,
            width: d.size,
            height: d.size,
            border: `1.5px solid ${d.color}`,
            opacity: d.opacity,
            transform: `translate(-50%, -50%) rotate(${d.rot}deg)`,
            boxSizing: 'border-box',
          }}
        />
      ))}
    </div>
  );
}

function Grain() {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 9999,
        opacity: 0.05,
        mixBlendMode: 'overlay',
        backgroundImage: GRAIN_BG,
        backgroundSize: '160px 160px',
      }}
    />
  );
}

function Chevron({ direction = 'left' }) {
  const path = direction === 'left' ? 'M15 6 9 12 15 18' : 'M9 6 15 12 9 18';
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" aria-hidden="true">
      <path d={path} />
    </svg>
  );
}

function ScreenshotCarousel({
  previews,
  previewIndex,
  onIndexChange,
  onRemove,
  onAddClick,
  canAdd,
  submitting,
}) {
  const hasImages = previews.length > 0;
  const hasMultiple = previews.length > 1;
  const current = previews[previewIndex];

  const goPrev = () => {
    if (!hasMultiple) return;
    onIndexChange((previewIndex - 1 + previews.length) % previews.length);
  };

  const goNext = () => {
    if (!hasMultiple) return;
    onIndexChange((previewIndex + 1) % previews.length);
  };

  return (
    <div className="rb-carousel">
      <div className="rb-carousel-stage">
        {hasImages ? (
          <>
            <img
              src={current}
              alt={`Screenshot ${previewIndex + 1} of ${previews.length}`}
              className="rb-carousel-image"
            />
            {hasMultiple && (
              <>
                <button
                  type="button"
                  className="rb-carousel-arrow rb-carousel-arrow-left"
                  onClick={goPrev}
                  aria-label="Previous screenshot"
                >
                  <Chevron direction="left" />
                </button>
                <button
                  type="button"
                  className="rb-carousel-arrow rb-carousel-arrow-right"
                  onClick={goNext}
                  aria-label="Next screenshot"
                >
                  <Chevron direction="right" />
                </button>
              </>
            )}
            <div className="rb-carousel-meta">
              <span>{previewIndex + 1} / {previews.length}</span>
              <button
                type="button"
                className="rb-carousel-remove"
                onClick={() => onRemove(previewIndex)}
                aria-label={`Remove screenshot ${previewIndex + 1}`}
              >
                Remove
              </button>
            </div>
          </>
        ) : (
          <div className="rb-carousel-empty">
            <div className="rb-carousel-empty-icon" aria-hidden="true">◇</div>
            <p>Add at least one screenshot so we can see the issue.</p>
            <span>JPG, PNG, or WebP · 2MB max each</span>
          </div>
        )}
      </div>

      {hasMultiple && (
        <div className="rb-carousel-dots" role="tablist" aria-label="Screenshot thumbnails">
          {previews.map((preview, index) => (
            <button
              key={preview}
              type="button"
              role="tab"
              aria-selected={index === previewIndex}
              aria-label={`View screenshot ${index + 1}`}
              className={`rb-carousel-dot${index === previewIndex ? ' is-active' : ''}`}
              onClick={() => onIndexChange(index)}
            >
              <img src={preview} alt="" />
            </button>
          ))}
        </div>
      )}

      <div className="rb-carousel-actions">
        <button
          type="button"
          className="rb-button"
          onClick={onAddClick}
          disabled={!canAdd || submitting}
        >
          {hasImages ? 'Add another screenshot' : 'Upload screenshot'}
        </button>
        <span className="rb-carousel-limit">{previews.length}/{BUG_REPORT_MAX_IMAGES} attached</span>
      </div>
    </div>
  );
}

function ReportBugStyles() {
  return (
    <style>{`
      @keyframes rbUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
      .rb-root { position: relative; min-height: 100%; overflow-x: hidden; color: ${C.text}; font-family: ${F.body}; background: ${C.bg}; }
      .rb-wash {
        position: fixed;
        inset: 0;
        pointer-events: none;
        z-index: 0;
        background:
          radial-gradient(circle at 20% 15%, rgba(232, 25, 44, 0.12), transparent 50%),
          radial-gradient(circle at 85% 80%, rgba(255, 107, 43, 0.07), transparent 55%),
          linear-gradient(180deg, ${C.bg} 0%, #090705 100%);
      }
      .rb-page { position: relative; z-index: 2; min-height: 100%; overflow-x: hidden; padding: 24px; animation: rbUp 360ms ease both; box-sizing: border-box; }
      .rb-shell { max-width: 1180px; margin: 0 auto; display: grid; gap: 18px; }
      .rb-hero {
        min-height: 210px;
        border-top: 1px solid ${C.border};
        border-right: 1px solid ${C.border};
        border-bottom: 1px solid ${C.border};
        border-left: 2px solid ${C.red};
        background: linear-gradient(135deg, ${C.card} 0%, ${C.cardAlt} 100%);
        box-shadow: 0 16px 48px rgba(0,0,0,0.28);
        position: relative;
        overflow: hidden;
        padding: 28px;
        display: grid;
        align-items: end;
      }
      .rb-hero::before {
        content: '';
        position: absolute;
        inset: 0;
        pointer-events: none;
        background:
          radial-gradient(circle at 14% 20%, rgba(232,25,44,.18), transparent 34%),
          radial-gradient(circle at 88% 0%, rgba(255,107,43,.1), transparent 28%);
      }
      .rb-hero-copy { position: relative; z-index: 1; max-width: 760px; }
      .rb-hero h1 { font-family: ${F.display}; font-size: clamp(48px, 6vw, 84px); letter-spacing: 7px; line-height: .9; margin: 8px 0 12px; font-weight: 400; text-shadow: 0 14px 34px rgba(0,0,0,.58); }
      .rb-hero h1 span { color: ${C.red}; }
      .rb-hero p { color: rgba(240,242,245,.72); font-size: 16px; line-height: 1.55; max-width: 640px; margin: 0; letter-spacing: .4px; }
      .rb-origin-pill {
        position: relative;
        z-index: 1;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin-top: 14px;
        padding: 8px 12px;
        border: 1px solid ${C.borderBright};
        background: rgba(8,8,8,.45);
        color: ${C.textDim};
        font-size: 11px;
        font-weight: 800;
        letter-spacing: 1.6px;
        text-transform: uppercase;
      }
      .rb-origin-pill span { color: ${C.text}; letter-spacing: .6px; text-transform: none; font-weight: 700; }
      .rb-grid { display: grid; grid-template-columns: minmax(300px, .92fr) minmax(380px, 1.08fr); gap: 18px; align-items: start; }
      .rb-card { background: ${C.card}; border-top: 1px solid ${C.border}; border-right: 1px solid ${C.border}; border-bottom: 1px solid ${C.border}; border-left: 2px solid ${C.red}; padding: 22px; min-width: 0; box-sizing: border-box; }
      .rb-card-alt { border-left-color: ${C.orange}; }
      .rb-card-head { margin-bottom: 18px; }
      .rb-card h2 { font-family: ${F.display}; font-size: 30px; letter-spacing: 4px; margin: 6px 0 0; line-height: 1; font-weight: 400; }
      .rb-eyebrow { display: flex; align-items: center; gap: 10px; color: ${C.textDim}; font-size: 10px; font-weight: 800; letter-spacing: 2.5px; text-transform: uppercase; }
      .rb-diamond { width: 7px; height: 7px; display: inline-block; background: ${C.red}; transform: rotate(45deg); }
      .rb-field { display: grid; gap: 8px; margin-bottom: 18px; }
      .rb-field label { color: ${C.textDim}; font-size: 10px; font-weight: 800; letter-spacing: 2.2px; text-transform: uppercase; }
      .rb-field select, .rb-field textarea { width: 100%; box-sizing: border-box; padding: 12px 14px; background: ${C.cardAlt}; color: ${C.text}; border: 1px solid ${C.border}; font-family: ${F.body}; font-size: 15px; line-height: 1.45; }
      .rb-field textarea { min-height: 180px; resize: vertical; }
      .rb-field-hint { font-size: 11px; color: ${C.textMuted}; letter-spacing: .4px; }
      .rb-field-hint.is-warn { color: ${C.orange}; }
      .rb-status { border: 1px solid ${C.border}; background: rgba(255,255,255,.03); color: ${C.textDim}; padding: 12px 14px; line-height: 1.45; letter-spacing: .4px; margin-bottom: 16px; }
      .rb-status.is-error { border-color: rgba(232,25,44,.42); background: rgba(232,25,44,.08); color: ${C.text}; }
      .rb-status.is-success { border-color: rgba(63,203,126,.34); background: rgba(63,203,126,.08); color: ${C.green}; }
      .rb-button { border: 1px solid ${C.borderBright}; background: #080808; color: ${C.text}; min-height: 42px; padding: 0 18px; font-family: ${F.body}; font-size: 11px; font-weight: 800; letter-spacing: 2px; text-transform: uppercase; cursor: pointer; }
      .rb-button-primary { border-color: rgba(232,25,44,.55); background: linear-gradient(135deg, ${C.red}, ${C.orange}); color: #fff; }
      .rb-button:disabled { opacity: .45; cursor: not-allowed; }
      .rb-submit-row { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; margin-top: 4px; }
      .rb-carousel { display: grid; gap: 14px; }
      .rb-carousel-stage { position: relative; min-height: clamp(280px, 52vh, 520px); border: 1px solid ${C.border}; background: radial-gradient(circle at 50% 0%, rgba(255,255,255,.04), transparent 42%), ${C.cardAlt}; overflow: hidden; display: flex; align-items: center; justify-content: center; }
      .rb-carousel-image { width: 100%; height: 100%; max-height: clamp(280px, 52vh, 520px); object-fit: contain; display: block; background: #080808; }
      .rb-carousel-empty { display: grid; justify-items: center; text-align: center; gap: 8px; padding: 28px; color: ${C.textDim}; }
      .rb-carousel-empty-icon { font-family: ${F.display}; font-size: 42px; color: ${C.red}; transform: rotate(45deg); line-height: 1; opacity: .8; }
      .rb-carousel-empty p { margin: 0; font-size: 15px; color: ${C.text}; letter-spacing: .3px; }
      .rb-carousel-empty span { font-size: 12px; color: ${C.textMuted}; letter-spacing: .8px; text-transform: uppercase; }
      .rb-carousel-arrow { position: absolute; top: 50%; transform: translateY(-50%); width: 42px; height: 42px; display: flex; align-items: center; justify-content: center; border: 1px solid ${C.borderBright}; background: rgba(8,8,8,.82); color: ${C.text}; cursor: pointer; z-index: 2; backdrop-filter: blur(4px); }
      .rb-carousel-arrow:hover { border-color: ${C.red}; color: ${C.red}; }
      .rb-carousel-arrow-left { left: 12px; }
      .rb-carousel-arrow-right { right: 12px; }
      .rb-carousel-meta { position: absolute; left: 12px; right: 12px; bottom: 12px; display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 8px 10px; background: rgba(8,8,8,.78); border: 1px solid ${C.border}; backdrop-filter: blur(4px); font-size: 11px; font-weight: 800; letter-spacing: 1.8px; text-transform: uppercase; color: ${C.textDim}; }
      .rb-carousel-remove { border: 1px solid rgba(232,25,44,.45); background: rgba(232,25,44,.1); color: ${C.red}; min-height: 30px; padding: 0 10px; font-family: ${F.body}; font-size: 10px; font-weight: 800; letter-spacing: 1.6px; text-transform: uppercase; cursor: pointer; }
      .rb-carousel-dots { display: flex; gap: 8px; overflow-x: auto; padding-bottom: 2px; }
      .rb-carousel-dot { flex: 0 0 72px; width: 72px; height: 52px; padding: 0; border: 1px solid ${C.border}; background: ${C.cardAlt}; cursor: pointer; opacity: .72; overflow: hidden; }
      .rb-carousel-dot.is-active { opacity: 1; border-color: ${C.red}; box-shadow: inset 2px 0 0 ${C.red}; }
      .rb-carousel-dot img { width: 100%; height: 100%; object-fit: cover; display: block; }
      .rb-carousel-actions { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
      .rb-carousel-limit { color: ${C.textMuted}; font-size: 11px; font-weight: 800; letter-spacing: 1.6px; text-transform: uppercase; }
      .rb-success-panel { display: grid; gap: 16px; }
      .rb-success-panel p { margin: 0; color: ${C.textDim}; line-height: 1.5; font-size: 15px; }
      @media (max-width: 980px) { .rb-grid { grid-template-columns: 1fr; } }
      @media (max-width: 640px) {
        .rb-page { padding: 16px; }
        .rb-hero { min-height: 180px; padding: 20px; }
        .rb-carousel-arrow { width: 36px; height: 36px; }
        .rb-carousel-arrow-left { left: 8px; }
        .rb-carousel-arrow-right { right: 8px; }
      }
      @media (prefers-reduced-motion: reduce) { .rb-page { animation: none; } }
    `}</style>
  );
}

export default function ReportBug() {
  const navigate = useNavigate();
  const location = useLocation();
  const issueContext = useMemo(() => resolveBugIssuePage(location), [location]);
  const [category, setCategory] = useState('other');
  const [description, setDescription] = useState('');
  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const trimmedLength = description.trim().length;

  const canSubmit = useMemo(() => (
    trimmedLength >= BUG_REPORT_MIN_CHARS
    && images.length >= 1
    && !submitting
    && !success
  ), [trimmedLength, images.length, submitting, success]);

  const canAddMore = images.length < BUG_REPORT_MAX_IMAGES;

  const previewsRef = useRef(previews);
  previewsRef.current = previews;

  useEffect(() => () => {
    previewsRef.current.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  const handleFiles = (fileList) => {
    setError('');
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;

    const combined = [...images, ...incoming];
    if (combined.length > BUG_REPORT_MAX_IMAGES) {
      setError(`You can attach up to ${BUG_REPORT_MAX_IMAGES} screenshots.`);
      return;
    }

    for (const file of incoming) {
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        setError('Screenshots must be JPG, PNG, or WebP.');
        return;
      }
      if (file.size > BUG_REPORT_MAX_BYTES) {
        setError('Each screenshot must be 2MB or smaller.');
        return;
      }
    }

    const newPreviews = incoming.map((file) => URL.createObjectURL(file));
    const wasEmpty = images.length === 0;
    setImages(combined);
    setPreviews((current) => [...current, ...newPreviews]);
    if (wasEmpty) setPreviewIndex(0);
    else setPreviewIndex(combined.length - 1);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index) => {
    setImages((current) => current.filter((_, i) => i !== index));
    setPreviews((current) => {
      const next = [...current];
      const [removed] = next.splice(index, 1);
      if (removed) URL.revokeObjectURL(removed);
      return next;
    });
    setPreviewIndex((current) => {
      const nextLength = previews.length - 1;
      if (nextLength <= 0) return 0;
      if (index < current) return current - 1;
      if (index === current) return Math.min(current, nextLength - 1);
      return current;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError('');
    try {
      await submitBugReport({
        description: description.trim(),
        category,
        pageUrl: issueContext.issuePageUrl || undefined,
        images,
      });
      setSuccess(true);
    } catch (submitError) {
      setError(extractErrorMessage(submitError));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ReportBugStyles />
      <div className="rb-root">
        <div className="rb-wash" aria-hidden="true" />
        <DriftField count={18} fixed />
        <Grain />
        <div className="rb-page">
          <div className="rb-shell">
          <header className="rb-hero">
            <div className="rb-hero-copy">
              <Eyebrow>Support</Eyebrow>
              <h1>REPORT A <span>BUG</span></h1>
              <p>
                Help us fix issues faster with a clear description and screenshots.
                {issueContext.originPath
                  ? ' We attach the Repeaked page you came from when you open this form.'
                  : ' Mention which page or match had the issue in your description.'}
              </p>
              {issueContext.originPath && (
                <div className="rb-origin-pill">
                  Issue page: <span>{issueContext.originPath}</span>
                </div>
              )}
            </div>
          </header>

          {success ? (
            <section className="rb-card">
              <div className="rb-card-head">
                <Eyebrow>Submitted</Eyebrow>
                <h2>THANK YOU</h2>
              </div>
              <div className="rb-success-panel">
                <div className="rb-status is-success">
                  Your bug report was sent to the Repeaked team. We will review it as soon as possible.
                </div>
                <p>If this was blocking you from playing, try refreshing or heading back home while we investigate.</p>
                <button type="button" className="rb-button rb-button-primary" onClick={() => navigate('/dashboard')}>
                  Back to home
                </button>
              </div>
            </section>
          ) : (
            <form className="rb-grid" onSubmit={handleSubmit}>
              <section className="rb-card">
                <div className="rb-card-head">
                  <Eyebrow>Details</Eyebrow>
                  <h2>WHAT HAPPENED?</h2>
                </div>

                <div className="rb-field">
                  <label htmlFor="bug-category">Category</label>
                  <select
                    id="bug-category"
                    value={category}
                    onChange={(event) => setCategory(event.target.value)}
                  >
                    {BUG_CATEGORIES.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                <div className="rb-field">
                  <label htmlFor="bug-description">Description</label>
                  <textarea
                    id="bug-description"
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                    maxLength={BUG_REPORT_MAX_CHARS}
                    placeholder="What happened? What did you expect instead?"
                  />
                  <div className={`rb-field-hint${trimmedLength < BUG_REPORT_MIN_CHARS ? ' is-warn' : ''}`}>
                    {trimmedLength}/{BUG_REPORT_MAX_CHARS} characters · minimum {BUG_REPORT_MIN_CHARS}
                  </div>
                </div>

                {error && <div className="rb-status is-error">{error}</div>}

                <div className="rb-submit-row">
                  <button type="submit" className="rb-button rb-button-primary" disabled={!canSubmit}>
                    {submitting ? 'Submitting…' : 'Submit report'}
                  </button>
                  <span className="rb-field-hint">One report every 15 minutes</span>
                </div>
              </section>

              <section className="rb-card rb-card-alt">
                <div className="rb-card-head">
                  <Eyebrow>Evidence</Eyebrow>
                  <h2>SCREENSHOTS</h2>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={(event) => handleFiles(event.target.files)}
                  style={{ display: 'none' }}
                />

                <ScreenshotCarousel
                  previews={previews}
                  previewIndex={previewIndex}
                  onIndexChange={setPreviewIndex}
                  onRemove={removeImage}
                  onAddClick={() => fileInputRef.current?.click()}
                  canAdd={canAddMore}
                  submitting={submitting}
                />
              </section>
            </form>
          )}
        </div>
        </div>
      </div>
    </>
  );
}
